import { DatePicker, Select, Tooltip } from 'antd';
import { AgencyTransactionBean, ListResponseTransactionBean, TransactionBean } from 'common/api/api.ts';
import { agencyBalanceApi } from 'common/api/entitiesApi.tsx';
import { ListCard } from 'common/components/ListCard.tsx';
import { AgencySelector } from 'common/components/reloadable/AgencySelector.tsx';
import { SearchInput } from 'common/components/SearchInput.tsx';
import { ListFilters, useAppContext } from 'common/context/AppContext.tsx';
import { COLUMN_ID_WIDTH } from 'common/util/constants.ts';
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts';
import { apiCall, transactionTypeLabel } from 'common/util/Util.tsx';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react'

import TransactionTypeEnum = AgencyTransactionBean.TransactionTypeEnum

const formatDollars = (dollars: number) => {
  return dollars.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
    useGrouping: true,
  })
}

const dateFormat = 'YYYY-MM-DD'
const TransactionType = [TransactionTypeEnum.BIDDINGSPENDS, TransactionTypeEnum.ADMINADDING, TransactionTypeEnum.ADMINSUBTRACTION, TransactionTypeEnum.AGENCYPAY]

const AgencyTransactionList = ({ _balance, admin }) => {
  const appContext = useAppContext()
  const [filters, setFilters] = useState<ListFilters>(initListFilters())
  const { setFilter, clearFilters } = useListFilters(setFilters)

  const [loading, setLoading] = useState(false)
  const [listData, setListData] = useState<ListResponseTransactionBean | undefined>()

  const loadData = () => {
    apiCall(
      setLoading,
      appContext,
      () =>
        agencyBalanceApi.agencyTransactionList(
          filters.sort?.name,
          filters.sort?.direction,
          admin ? filters.name : undefined,
          filters.startInterval?.format(dateFormat),
          filters.endInterval?.format(dateFormat),
          admin ? filters.agencyId : undefined,
          filters.type,
          { query: { page: filters.page, size: filters.pageSize } },
        ),
      (data) => setListData(data),
    )
  }

  useEffect(() => () => clearFilters(), [])

  return (
    <ListCard
      loading={loading}
      title={admin ? 'Transactions' : 'Billing History'}
      filters={filters}
      setFilters={setFilters}
      listData={listData}
      loadData={loadData}
      {...(admin && {
        createName: 'Operation',
        createRoute: '/agency-transactions/create',
      })}
      columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          width: COLUMN_ID_WIDTH,
        },
        admin
          ? {
              title: 'Agency',
              dataIndex: 'agencyName',
              className: 'list-table-name',
              ellipsis: true,
            }
          : null,
        {
          title: 'Creation',
          dataIndex: 'created',
          sorter: true,
          render: (date: string) => dayjs(date).format('YYYY-MM-DD HH:mm:ss'),
          width: 200,
        },
        {
          title: 'Type',
          dataIndex: 'transactionType',
          render: (type: string) => transactionTypeLabel[type],
          width: 180,
        },
        {
          title: 'Credit',
          render: (_: any, record: TransactionBean) =>
            (record.amountBefore ?? 0) >= (record.amountAfter ?? 0)
              ? ''
              : `${record.amount && formatDollars(record.amount)}`,
          width: 120,
        },
        {
          title: 'Debit',
          render: (_: any, record: TransactionBean) =>
            (record.amountBefore ?? 0) < (record.amountAfter ?? 0)
              ? ''
              : `-${record.amount && formatDollars(record.amount)}`,
          width: 120,
        },
        {
          title: 'Note',
          dataIndex: 'note',
          render: (note: string) => <Tooltip title={note}>{note}</Tooltip>,
          ellipsis: true,
          width: 220,
        },
        {
          title: 'Balance before',
          dataIndex: 'amountBefore',
          render: (amount: number) => formatDollars(amount),
          width: 120,
        },
        {
          title: 'Balance after',
          dataIndex: 'amountAfter',
          render: (amount: number) => formatDollars(amount),
          width: 120,
        },
      ]}
    >
      {admin && <SearchInput value={filters.name} onChange={(val) => setFilter('name', val)} />}
      {admin && (
        <AgencySelector
          placeholder="Agency"
          style={{ minWidth: '200px' }}
          value={filters.advertiserId}
          onChange={(value: number) => setFilter('agencyId', value)}
        />
      )}
      <DatePicker
        placeholder="Start Date"
        value={filters.startInterval}
        onChange={(value) => setFilter('startInterval', value ?? undefined)}
        disabledDate={(value) => !!filters.endInterval && dayjs(value).isAfter(dayjs(filters.endInterval))}
        allowClear
      />
      <DatePicker
        placeholder="End Date"
        value={filters.endInterval}
        onChange={(value) => setFilter('endInterval', value ?? undefined)}
        disabledDate={(value) => !!filters.startInterval && dayjs(value).isBefore(dayjs(filters.startInterval))}
        allowClear
      />
      <Select
        style={{ minWidth: 180 }}
        value={filters.type}
        placeholder="Transaction type"
        allowClear
        onChange={(value) => setFilter('type', value)}
        options={Object.values(TransactionType)
          .sort((a, b) => transactionTypeLabel[a].localeCompare(transactionTypeLabel[b]))
          .map((type) => ({
            value: type,
            label: transactionTypeLabel[type],
          }))}
      />
    </ListCard>
  )
}

export default AgencyTransactionList
